import React, { useState, useEffect } from 'react';
import { X, Info, Code, AlertTriangle } from 'lucide-react';
import { useUpdateEmailTemplate, useDeleteEmailTemplate, availableMergeFields, extractMergeFields, type EmailTemplate, type TemplateCategory } from '../../../services/queries/emailTemplateQueries';
import { useToast } from '../../../contexts/ToastContext';
import { StandardButton, StandardInput, StandardSelect, StandardCheckbox } from '../../standard';

interface EditTemplateModalProps {
  template: EmailTemplate | null;
  isOpen: boolean;
  onClose: () => void;
}

export const EditTemplateModal: React.FC<EditTemplateModalProps> = ({ template, isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    body: '',
    category: 'custom' as TemplateCategory,
    description: '',
    isActive: true
  });

  const [showMergeFields, setShowMergeFields] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const updateTemplateMutation = useUpdateEmailTemplate();
  const deleteTemplateMutation = useDeleteEmailTemplate();
  const { success, error } = useToast();

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name,
        subject: template.subject,
        body: template.body,
        category: template.category,
        description: template.description || '',
        isActive: template.isActive
      });
    }
  }, [template]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!template) return;

    try {
      // Extract merge fields from subject and body
      const subjectFields = extractMergeFields(formData.subject);
      const bodyFields = extractMergeFields(formData.body);
      const allMergeFields = Array.from(new Set([...subjectFields, ...bodyFields]));

      await updateTemplateMutation.mutateAsync({
        templateId: template.id,
        updates: {
          ...formData,
          mergeFields: allMergeFields
        }
      });

      success('Template updated successfully!');
      onClose();
    } catch (err: any) {
      error('Failed to update template', err.message || 'Please try again');
    }
  };

  const handleDelete = async () => {
    if (!template) return;

    try {
      await deleteTemplateMutation.mutateAsync(template.id);
      success('Template deleted successfully!');
      onClose();
      setShowDeleteConfirm(false);
    } catch (err: any) {
      error('Failed to delete template', err.message || 'Please try again');
    }
  };

  const insertMergeField = (field: string) => {
    const textarea = document.getElementById('edit-template-body') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = formData.body;
      const before = text.substring(0, start);
      const after = text.substring(end);

      setFormData(prev => ({
        ...prev,
        body: before + field + after
      }));

      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + field.length, start + field.length);
      }, 0);
    }
  };

  if (!isOpen || !template) return null;

  const mergeFieldsByCategory = availableMergeFields.reduce((acc, field) => {
    if (!acc[field.category]) {
      acc[field.category] = [];
    }
    acc[field.category].push(field);
    return acc;
  }, {} as Record<string, typeof availableMergeFields>);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Edit Email Template</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              {template.name}
              {template.isSystem && <span className="ml-2 text-yellow-600">(System Template)</span>}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {template.isSystem && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <p className="text-sm text-yellow-800 dark:text-yellow-300">
                  <strong>System Template:</strong> This is a system template. You can modify the content but cannot delete it or deactivate it.
                </p>
              </div>
            )}

            {/* Version Info */}
            <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
              <div className="flex items-center justify-between text-sm">
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Version: </span>
                  <span className="font-semibold text-gray-900 dark:text-white">{template.version}</span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Last Modified: </span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {new Date(template.lastModified).toLocaleDateString()}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Times Sent: </span>
                  <span className="font-semibold text-gray-900 dark:text-white">{template.usageCount}</span>
                </div>
              </div>
            </div>

            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Basic Information</h3>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <StandardInput
                    label="Template Name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    disabled={template.isSystem}
                  />

                  <StandardSelect
                    label="Category"
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as TemplateCategory }))}
                    disabled={template.isSystem}
                    options={[
                      { value: 'user', label: 'User' },
                      { value: 'client', label: 'Client' },
                      { value: 'case', label: 'Case' },
                      { value: 'notification', label: 'Notification' },
                      { value: 'custom', label: 'Custom' }
                    ]}
                  />
                </div>

                <StandardInput
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>
            </div>

            {/* Email Content */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Email Content</h3>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800 dark:text-blue-300">
                    Editing this template will create version {template.version + 1}. Previous versions are not stored in this demo.
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <StandardInput
                  label="Subject Line"
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  className="font-mono text-sm"
                />

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Email Body
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowMergeFields(!showMergeFields)}
                      className="px-3 py-1 text-sm text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors flex items-center gap-1"
                    >
                      <Code className="w-4 h-4" />
                      {showMergeFields ? 'Hide' : 'Show'} Merge Fields
                    </button>
                  </div>

                  <div className="grid grid-cols-1 gap-4" style={{ gridTemplateColumns: showMergeFields ? '1fr 300px' : '1fr' }}>
                    <textarea
                      id="edit-template-body"
                      value={formData.body}
                      onChange={(e) => setFormData(prev => ({ ...prev, body: e.target.value }))}
                      rows={12}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
                    />

                    {showMergeFields && (
                      <div className="border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 p-3 overflow-y-auto max-h-[400px]">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                          Available Merge Fields
                        </h4>
                        {Object.entries(mergeFieldsByCategory).map(([category, fields]) => (
                          <div key={category} className="mb-4">
                            <h5 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-2">
                              {category}
                            </h5>
                            <div className="space-y-1">
                              {fields.map(field => (
                                <button
                                  key={field.id}
                                  type="button"
                                  onClick={() => insertMergeField(field.value)}
                                  className="w-full text-left px-2 py-1.5 text-xs hover:bg-gray-200 dark:hover:bg-gray-800 rounded transition-colors"
                                >
                                  <div className="font-medium text-gray-900 dark:text-white">
                                    {field.label}
                                  </div>
                                  <div className="text-gray-500 dark:text-gray-400 font-mono">
                                    {field.value}
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Status */}
            <StandardCheckbox
              label="Active - Template can be used"
              checked={formData.isActive}
              onChange={(checked) => setFormData(prev => ({ ...prev, isActive: checked }))}
              disabled={template.isSystem}
            />

            {/* Delete Template Section */}
            {!template.isSystem && (
              <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Danger Zone</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                  Deleting this template is permanent and cannot be undone.
                </p>
                {!showDeleteConfirm ? (
                  <StandardButton
                    type="button"
                    variant="danger"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    Delete Template
                  </StandardButton>
                ) : (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <div className="flex items-start gap-3 mb-4">
                      <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-red-900 dark:text-red-300 mb-1">Are you sure?</p>
                        <p className="text-sm text-red-800 dark:text-red-400">
                          This will permanently delete "{template.name}".
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <StandardButton
                        type="button"
                        variant="danger"
                        onClick={handleDelete}
                        disabled={deleteTemplateMutation.isPending}
                        isLoading={deleteTemplateMutation.isPending}
                      >
                        {deleteTemplateMutation.isPending ? 'Deleting...' : 'Yes, Delete Template'}
                      </StandardButton>
                      <StandardButton
                        type="button"
                        variant="ghost"
                        onClick={() => setShowDeleteConfirm(false)}
                      >
                        Cancel
                      </StandardButton>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3 bg-gray-50 dark:bg-gray-900">
          <StandardButton
            type="button"
            variant="ghost"
            onClick={onClose}
          >
            Cancel
          </StandardButton>
          <StandardButton
            variant="primary"
            onClick={handleSubmit}
            disabled={updateTemplateMutation.isPending}
            isLoading={updateTemplateMutation.isPending}
          >
            {updateTemplateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </StandardButton>
        </div>
      </div>
    </div>
  );
};
